import { useMemo } from 'react';

import { useAppSelector } from '@/store/hooks';
import { selectIsSingleSession, selectSessionByUuid } from '@/store/slices/project/selectors';
import { ModuleGroupingTypes, PageModule, PageModuleType } from '@/types/module.types';
import { Speaker } from '@/types/speakers.types';
import { assertIsSpeaker } from '../assertions/assert-is-speaker';
import { selectSpeakersById } from '@/store/slices/speakers/selectors';
import useLoadSpeakersByIds from '@/connection/hooks/use-load-speakers-by-ids';
import { quietError } from '../logging';

type Props = {
  sessionUuid?: string;
};

const useSpeakersForSession = ({ sessionUuid }: Props) => {
  const session = useAppSelector(selectSessionByUuid(sessionUuid));
  const speakersById = useAppSelector(selectSpeakersById);
  const isSingleSession = useAppSelector(selectIsSingleSession);

  const sessionSpeakers = useMemo(() => {
    if (!session) return [];

    const syncSpeakersIsOn =
      isSingleSession &&
      (session.modules || []).some((m: PageModule) => m.type === PageModuleType.speakers && m.content?.sync_speakers);

    const excludedModuleIds = syncSpeakersIsOn
      ? new Set(
          (session.module_grouping ?? [])
            .filter((g) => g.type === ModuleGroupingTypes.Extras || g.type === ModuleGroupingTypes.custom)
            .flatMap((g) => g.modules)
        )
      : new Set<number>();

    const [speakers] = (session?.modules || []).reduce<[Speaker[], Set<number>]>(
      (acc, module: PageModule) => {
        if (!module.is_on) return acc;
        if (module.type !== PageModuleType.speakers) return acc;
        if (!module.content_modules?.length) return acc;
        if (syncSpeakersIsOn) {
          if (module.is_custom) return acc;
          if (module.id !== undefined && excludedModuleIds.has(module.id)) return acc;
        }

        const [accumSpeakers, uniqueSpeakerIds] = acc;
        (module.modules || []).forEach((speaker: Speaker) => {
          try {
            assertIsSpeaker(speaker);
            if (uniqueSpeakerIds.has(speaker.speaker)) return;
            uniqueSpeakerIds.add(speaker.speaker);
            accumSpeakers.push(speaker);
          } catch (e) {
            // this error does not mean much, it's just an improperly formatted speaker, likely from early dev.
            // can safely log the error quietly and look only if we really care.
            quietError(e);
          }
        });

        return acc;
      },
      [[], new Set<number>()]
    );

    return speakers;
  }, [isSingleSession, session]);

  const sessionSpeakerIds = useMemo(() => sessionSpeakers.map((s) => s.speaker), [sessionSpeakers]);

  useLoadSpeakersByIds(sessionSpeakerIds);

  return useMemo(() => {
    return sessionSpeakers.map((speaker) => ({
      ...speaker,
      bl_admin: speakersById[speaker.speaker]?.bl_admin || null,
    }));
  }, [sessionSpeakers, speakersById]);
};

export default useSpeakersForSession;
